/* Independent module: unaffected by errors in the theme's shared block-scripts bundle. */
if (!customElements.get('ghost-product-reviews')) {
  class GhostProductReviews extends HTMLElement {
    static observedAttributes = ['data-product-id', 'data-app-key', 'data-page-size', 'data-heading', 'data-show-product'];
    constructor() {
      super();
      // Theme HTML morphs cannot erase the rendered reviews inside this root.
      this.attachShadow({mode: 'open'});
      this.page = 1;
      this.sort = 'date';
      this.sequence = 0;
      this.selection = 0;
      this.onProductSelect = this.onProductSelect.bind(this);
    }
    connectedCallback() {
      this.build();
      document.addEventListener('shopify:product:select', this.onProductSelect);
      this.load();
    }
    disconnectedCallback() {
      document.removeEventListener('shopify:product:select', this.onProductSelect);
      this.controller?.abort();
      this.sequence++;
      this.selection++;
    }
    attributeChangedCallback(name, oldValue, value) {
      if (oldValue === value || !this.isConnected || !this.list) return;
      this.page = 1;
      clearTimeout(this.attributeTimer);
      this.attributeTimer = setTimeout(() => { if (this.isConnected) { this.build(); this.load(); } }, 0);
    }
    async onProductSelect(event) {
      const picker = event.target.closest?.('variant-picker');
      if (!picker || picker.closest('product-card, quick-add-dialog')) return;
      const featured = picker.closest('featured-product-information');
      if (featured ? !featured.contains(this) : picker.dataset.templateProductMatch !== 'true') return;
      const selection = ++this.selection;
      try {
        const result = await event.promise;
        if (!this.isConnected || selection !== this.selection) return;
        // The resolved result is for the new child; event.product is the OLD product.
        const id = result?.detail?.newProduct?.id || result?.detail?.productId;
        if (id) this.setAttribute('data-product-id', String(id).split('/').pop());
      } catch (_) { /* A cancelled product selection must not change the review feed. */ }
    }
    el(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }
    plain(value) {
      // Decode entities as inert text; never insert review HTML into the live page.
      const text = document.createElement('textarea');
      text.innerHTML = String(value ?? '').replace(/</g, '&lt;');
      return text.value;
    }
    moons(score) {
      const wrap = this.el('span', 'moons');
      wrap.setAttribute('role', 'img');
      wrap.setAttribute('aria-label', `${Number(score).toFixed(1)} out of 5`);
      for (let i = 0; i < 5; i++) {
        const moon = this.el('span', 'moon');
        const fill = this.el('span', 'moon-fill');
        fill.style.width = `${Math.max(0, Math.min(1, score - i)) * 100}%`;
        moon.append(fill); wrap.append(moon);
      }
      return wrap;
    }
    build() {
      this.shadowRoot.replaceChildren();
      const css = this.el('link'); css.rel = 'stylesheet'; css.href = this.dataset.css;
      const region = this.el('section', 'reviews');
      region.setAttribute('aria-label', this.dataset.heading || 'Customer reviews');
      const heading = this.el('h2', 'heading', this.dataset.heading || 'Customer reviews');
      this.summary = this.el('div', 'summary');
      const controls = this.el('div', 'controls');
      const label = this.el('label', '', 'Sort reviews ');
      this.sortSelect = this.el('select');
      for (const [value, title] of [['date','Newest first'], ['rating','Highest rated'], ['votes_up','Most helpful']]) {
        const option = this.el('option', '', title); option.value = value; this.sortSelect.append(option);
      }
      this.sortSelect.value = this.sort;
      this.sortSelect.addEventListener('change', () => {this.sort = this.sortSelect.value; this.page = 1; this.load();});
      label.append(this.sortSelect); controls.append(label);
      this.status = this.el('p', 'status'); this.status.setAttribute('role','status'); this.status.setAttribute('aria-live','polite');
      this.retry = this.el('button', '', 'Try again'); this.retry.type = 'button'; this.retry.hidden = true;
      this.retry.addEventListener('click', () => this.load());
      this.list = this.el('div', 'list');
      this.previous = this.el('button', '', 'Previous'); this.previous.type = 'button';
      this.next = this.el('button', '', 'Next'); this.next.type = 'button';
      this.pageLabel = this.el('span');
      this.previous.addEventListener('click', () => { if(this.page > 1) {this.page--; this.load(true);} });
      this.next.addEventListener('click', () => {this.page++; this.load(true);});
      this.pager = this.el('nav', 'pager'); this.pager.setAttribute('aria-label','Review pages');
      this.pager.append(this.previous, this.pageLabel, this.next);
      region.append(heading, this.summary, controls, this.status, this.retry, this.list, this.pager);
      this.shadowRoot.append(css, region);
    }
    async load(scroll = false) {
      const sequence = ++this.sequence;
      this.controller?.abort(); this.controller = new AbortController();
      this.status.textContent = 'Loading reviews…'; this.retry.hidden = true;
      this.list.replaceChildren(); this.summary.replaceChildren(); this.pager.hidden = true;
      this.setAttribute('aria-busy','true');
      try {
        if (!this.dataset.appKey || !this.dataset.productId) throw Error('Missing configuration');
        const perPage = Math.min(15, Math.max(3, Number(this.dataset.pageSize) || 5));
        const url = new URL(`https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(this.dataset.appKey)}/products/${encodeURIComponent(this.dataset.productId)}/reviews.json`);
        url.search = new URLSearchParams({per_page:perPage, page:this.page, sort:this.sort, direction:'desc'});
        const response = await fetch(url, {signal:this.controller.signal, credentials:'omit'});
        if (!response.ok) throw Error('Request failed');
        const payload = await response.json();
        if (sequence !== this.sequence || !this.isConnected) return;
        if (payload.status?.code !== 200 || !payload.response) throw Error('Invalid response');
        const data = payload.response;
        const total = Number(data.bottomline?.total_review ?? data.pagination?.total ?? 0);
        const average = Number(data.bottomline?.average_score || 0);
        const score = this.el('span','average',total ? average.toFixed(1) : '—');
        const summaryText = this.el('div');
        summaryText.append(this.moons(average), this.el('p','',`Based on ${total.toLocaleString()} ${total === 1 ? 'review' : 'reviews'}`));
        this.summary.append(score, summaryText);
        const seen = new Set();
        for (const review of data.reviews || []) {
          if (review.deleted || seen.has(review.id)) continue;
          seen.add(review.id);
          this.list.append(this.renderReview(review, data.grouping_data?.[review.id]));
        }
        const pages = Math.max(1, Math.ceil(Number(data.pagination?.total ?? total) / perPage));
        this.previous.disabled = this.page <= 1; this.next.disabled = this.page >= pages;
        this.pageLabel.textContent = `Page ${this.page} of ${pages}`;
        this.pager.hidden = pages <= 1;
        this.status.textContent = total ? `Showing page ${this.page} of customer reviews.` : 'No reviews for this product yet.';
        this.status.classList.toggle('sr-only', total > 0);
        if (scroll) {this.scrollIntoView({block:'start',behavior:'smooth'}); this.pageLabel.tabIndex = -1; this.pageLabel.focus({preventScroll:true});}
      } catch (error) {
        if (sequence !== this.sequence || error.name === 'AbortError') return;
        this.status.classList.remove('sr-only');
        this.status.textContent = 'Reviews could not be loaded. Please try again.'; this.retry.hidden = false;
      } finally {if (sequence === this.sequence) this.removeAttribute('aria-busy');}
    }
    renderReview(review, group) {
      const row = this.el('article','review');
      const author = this.el('div','author');
      const name = this.plain(review.user?.display_name || 'Customer');
      const avatar = this.el('span','avatar',name.trim().slice(0,1).toUpperCase()); avatar.setAttribute('aria-hidden','true');
      const identity = this.el('div'); identity.append(this.el('p','name',name));
      if (review.verified_buyer) identity.append(this.el('span','verified','✓ Verified buyer'));
      author.append(avatar,identity);
      const content = this.el('div','content');
      const title = this.el('div','review-title'); title.append(this.moons(Number(review.score)||0));
      if (review.title) title.append(this.el('h3','',this.plain(review.title)));
      content.append(title,this.el('p','body',this.plain(review.content)));
      if (this.dataset.showProduct === 'true' && group?.product_name) content.append(this.el('p','product-name',`Reviewed: ${this.plain(group.product_name)}`));
      if (review.is_incentivized) content.append(this.el('p','disclosure','Incentivized review'));
      if (review.comment?.content) {
        const reply = this.el('div','reply'); reply.append(this.el('strong','','Store response'),this.el('p','',this.plain(review.comment.content))); content.append(reply);
      }
      const time = this.el('time','date'); const date = new Date(review.created_at);
      if (!Number.isNaN(date.getTime())) {time.dateTime=date.toISOString(); time.textContent=new Intl.DateTimeFormat('en-GB',{day:'2-digit',month:'2-digit',year:'2-digit'}).format(date);}
      row.append(author,content,time);
      const helpful = Number(review.votes_up)||0;
      if (helpful > 0) row.append(this.el('p','helpful',`${helpful} ${helpful === 1 ? 'person found' : 'people found'} this helpful`));
      return row;
    }
  }
  customElements.define('ghost-product-reviews', GhostProductReviews);
}
